import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { EASE } from "./Motion";

const NAV = [
  { to: "/services", label: "Services" },
  { to: "/learn", label: "Learn" },
  { to: "/our-work", label: "Our Work" },
  { to: "/about", label: "About" },
  { to: "/support", label: "Support" },
];

function Wordmark() {
  return (
    <Link to="/" data-testid="logo-link" className="flex items-baseline gap-1.5">
      <span className="font-serif text-2xl sm:text-3xl font-bold tracking-tight text-charcoal">
        Pro<span className="italic text-teal">One</span>
      </span>
      <span className="mb-0.5 h-2 w-2 rotate-45 bg-teal" aria-hidden="true" />
    </Link>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname, location.search]);

  return (
    <header
      className="fixed inset-x-0 top-0 z-50 border-b border-line/70 bg-cream/85 backdrop-blur-md"
      data-testid="site-header"
    >
      <div className="mx-auto flex h-16 sm:h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Wordmark />
        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}-link`}
              className={({ isActive }) =>
                `relative text-sm font-semibold tracking-wide transition-colors duration-300 hover:text-teal ${
                  isActive ? "text-teal" : "text-charcoal/80"
                } after:absolute after:-bottom-1.5 after:left-0 after:h-px after:bg-teal after:transition-all after:duration-300 ${
                  isActive ? "after:w-full" : "after:w-0 hover:after:w-full"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          <Link to="/contact?intent=Hire" data-testid="header-hire-us-btn" className="btn-primary !px-5 !py-2.5">
            Hire Us
          </Link>
          <Link to="/support?tab=donate" data-testid="header-donate-btn" className="btn-outline !px-5 !py-2.5">
            Donate
          </Link>
        </div>
        <button
          className="lg:hidden flex h-11 w-11 items-center justify-center rounded-full border border-line text-charcoal"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="lg:hidden border-t border-line bg-cream px-4 pb-8 pt-4"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col gap-1" aria-label="Mobile">
              {NAV.map((n, i) => (
                <motion.div
                  key={n.to}
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 + i * 0.05, duration: 0.4, ease: EASE }}
                >
                  <NavLink
                    to={n.to}
                    data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                    className={({ isActive }) =>
                      `block rounded-xl px-4 py-3 font-serif text-2xl ${
                        isActive ? "bg-sand text-teal" : "text-charcoal"
                      }`
                    }
                  >
                    {n.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <div className="mt-5 flex gap-3 px-4">
              <Link to="/contact?intent=Hire" data-testid="mobile-hire-us-btn" className="btn-primary flex-1">
                Hire Us
              </Link>
              <Link to="/support?tab=donate" data-testid="mobile-donate-btn" className="btn-outline flex-1">
                Donate
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
